<template>
  <div v-if="companyLoaded">
    <div class="flex items-center justify-between px-10 py-8 bg-gray-100 border-b rounded-t-lg">
      <h1 class="text-xl font-semibold tracking-wider text-gray-800 uppercase">
        Untergesellschaft erstellen
      </h1>
      <router-link
        :to="`/companies/${companyId}`"
        class="font-semibold text-gray-600 uppercase hover:text-gray-800"
      >
        Abbrechen
      </router-link>
    </div>
    <ValidationObserver
      ref="validationObserver"
      v-slot="{ validated, invalid }"
    >
      <div
        id="wizard"
        class="p-10 pt-0"
      >
        <div
          v-if="validated && invalid"
          class="flex mt-4"
        >
          <div class="w-1/3" />
          <div class="w-2/3">
            <alert type="error">
              Bitte überprüfe die markierten Felder
            </alert>
          </div>
        </div>
        <section>
          <div class="w-1/3 description">
            <h1>Grunddaten</h1>
            <p>Alle weiteren Daten werden über den Onboarding-Wizard erfasst.</p>

            <p>
              Alle Seiten werden unter folgender Domain ausgespielt:
              <span class="font-semibold text-gray-700">
                {{ companyDomain }}</span>
            </p>
          </div>
          <div class="w-2/3">
            <div class="flex flex-wrap mb-6">
              <div class="w-1/2 px-2 form-group">
                <input-text-validated
                  ref="company.slug"
                  v-model="company.slug"
                  input-class="w-full"
                  label="Eindeutiges Firmenkürzel (Slug)"
                  placeholder="max-makler-immmobilien"
                  rules="required|slug"
                />
              </div>
              <div class="w-1/2 px-2 form-group">
                <input-text-validated
                  ref="company.name"
                  v-model="company.name"
                  input-class="w-full"
                  label="Firmenname"
                  placeholder="Max Makler Immobilien"
                  rules="required"
                />
              </div>
            </div>
            <div class="flex flex-wrap mb-6">
              <div
                v-if="countriesForCompany.length > 1"
                class="w-1/2 px-2 form-group"
              >
                <input-select-validated
                  ref="company.country"
                  v-model="company.country"
                  :options="countriesForCompany"
                  label="Länderkennung"
                  rules="required"
                  @change="onCountryChange"
                />
                <p class="pl-1 text-xs text-gray-700">
                  Kann nachträglich nicht mehr geändert werden
                </p>
              </div>
              <Feature
                v-slot="{feature}"
                :feature-slug="featureNames.VARIANT"
              >
                <div
                  v-if="hasMultipleVariants(feature.config.variants)"
                  class="w-1/2 px-2 form-group"
                >
                  <input-select-validated
                    ref="company.variant"
                    v-model="company.variant"
                    :options="variants"
                    label="Variante"
                    rules="required"
                    :disabled="!company.country"
                  />
                  <p
                    v-if="!company.country"
                    class="pl-1 text-xs text-gray-700 "
                  >
                    Zuerst Land auswählen
                  </p>
                </div>
              </Feature>
            </div>
          </div>
        </section>
        <section>
          <div class="w-1/3 description">
            <h1>Billwerk</h1>
            <p>Neue Child Company Komponente in Billwerk buchen</p>
          </div>
          <div class="w-2/3">
            <BillwerkOrder
              create
              child-company
              :order-data="orderData"
              :bookable-sales-plans="[]"
            />
          </div>
        </section>
        <div
          v-if="graphQLErrors.length > 0"
          class="flex mt-4"
        >
          <div class="w-1/3" />
          <div class="w-1/3">
            <alert type="error">
              Die Firma konnte nicht erstellt werden:
              <ul>
                <li
                  v-for="error in graphQLErrors"
                  :key="error"
                >
                  {{ error }}
                </li>
              </ul>
            </alert>
          </div>
        </div>

        <div class="flex justify-center">
          <div class="pt-8">
            <button
              :disabled="isSubmitting"
              class="btn-primary"
              @click="submit()"
            >
              <span v-if="isSubmitting">Immobilienfirma wird erstellt</span>
              <span v-else>Immobilienfirma erstellen</span>
            </button>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>
<script>
import Alert from '@/components/alert.vue'
import { Feature } from 'feature-config'
import InputTextValidated from '@/components/form/text-validated.vue'
import InputSelectValidated from '@/components/form/select-validated.vue'
import CompanyService from '@/pages/companies/services/CompanyService'
import GET_COMPANY from '@/graphql/GetCompany.gql'
import GET_AVAILABLE_CAMPAIGNS from '@/graphql/AvailableCampaigns.gql'
import { COUNTRIES_FOR_COMPANY } from '@/config'
import featureMixin from '@/mixins/feature'
import brandingMixin from '@/mixins/branding'
import variantMixin from '@/mixins/variant'
import BillwerkOrder from '@/components/form/billwerk-order.vue'
import CREATE_CHILD_COMPANY from '@/graphql/CreateChildCompany.gql'
export default {
  components: { BillwerkOrder, InputSelectValidated, InputTextValidated, Feature, Alert },
  mixins: [
    brandingMixin,
    featureMixin,
    variantMixin
  ],
  data () {
    return {
      graphQLErrors: [],
      isSubmitting: false,
      countriesForCompany: [],
      variants: [],
      company: {
        slug: '',
        name: '',
        country: '',
        variant: null,
        platform: '',
        salesPerson: { id: '' },
        clientAdvisor: { id: '' },
        productType: '',
        ssoProviders: [],
        components: []
      },
      companyLoaded: false,
      orderData: {
        plan: '',
        planVariant: '',
        discount: '',
        components: [],
        contractId: '',
        customerId: '',
        startDate: null
      }
    }
  },
  computed: {
    availableComponents () {
      return this.$features.feature(this.featureNames.COMPANY).config.availableComponents?.map((type) => ({ value: type, name: type }))
    },
    companyDomain () {
      return this.company?.slug
        ? `${this.company.slug}.${this.landingpageBaseDomain}`
        : `<max-makler-immobilien>.${this.landingpageBaseDomain}`
    },
    companyId () {
      return this.$route.params.id
    }
  },
  watch: {
    parentCompany (newVal) {
      if (newVal) {
        this.company = { ...newVal }
        this.init()
      }
    }
  },
  created () {
    if (CompanyService.getCompany()) {
      this.company = CompanyService.getCompany()
      this.init()
    }
  },
  methods: {
    init () {
      this.variants = this.getMappedVariants()
      this.countriesForCompany = COUNTRIES_FOR_COMPANY.filter(({ value }) =>
        this.$features.feature(this.featureNames.COMPANY).config.availableCountryCodes.includes(value)
      )
      if (this.variants.length === 1) {
        this.company.variant = this.variants[0].value
      }
      if (this.countriesForCompany.length === 1) {
        this.company.country = this.countriesForCompany[0].value
      }

      this.companyLoaded = true
    },
    async submit () {
      const isValid = await this.$refs.validationObserver.validate()

      if (!isValid) {
        window.scrollTo({ top: 0 })
        return
      }

      try {
        this.isSubmitting = true
        const { country, slug, name, variant, platform, salesPerson, clientAdvisor, productType, ssoProviders, components } = this.company
        const { data: { company } } = await this.$apollo.mutate({
          mutation: CREATE_CHILD_COMPANY,
          variables: {
            input: {
              country,
              slug,
              name,
              variant,
              platform,
              salesPerson: salesPerson?.id,
              clientAdvisor: clientAdvisor?.id,
              productType,
              ssoProviders,
              components,
              parentId: this.companyId,
              startDate: this.orderData.startDate
            }
          }
        })

        this.$router.push({
          path: `/companies/${company.id}`
        })

        window.scrollTo({ top: 0 })
        this.isSubmitting = false
      } catch (error) {
        if (error.graphQLErrors) {
          this.graphQLErrors = error.graphQLErrors.map((error) => error.message)
        }
        this.setValidationErrors(error, 'company')
        window.scrollTo({ top: 0 })
        this.isSubmitting = false
      }
    },
    onCountryChange () {
      this.company.variant = this.variants[0].value
    }
  },
  apollo: {
    parentCompany: {
      query: GET_COMPANY,
      variables () {
        return {
          id: this.companyId
        }
      },
      update (data) {
        if (!data || !data.company) {
          return null
        }
        return data.company
      },
      skip () {
        return CompanyService.getCompany() || !this.companyId
      }
    },
    availableCampaigns: {
      query: GET_AVAILABLE_CAMPAIGNS,
      variables () {
        return {
          companyId: this.companyId
        }
      },
      skip () {
        return !this.companyId
      }
    }
  },
  unmounted () {
    CompanyService.clearCompany()
  }
}
</script>
